import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Form, Input } from '@heroui/react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/ui/theme'

// returns-api rejects anything longer.
const MAX_LENGTH = 255
const tooLong = { message: 'withdrawal.too_long' }

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'withdrawal.name_required' })
    .max(MAX_LENGTH, tooLong),
  orderNumber: z
    .string()
    .trim()
    .min(1, { message: 'withdrawal.order_number_required' })
    .max(MAX_LENGTH, tooLong),
  email: z
    .email({ message: 'withdrawal.email_invalid' })
    .max(MAX_LENGTH, tooLong)
})

export type WithdrawalFormData = z.infer<typeof schema>

export const WithdrawalForm = ({
  defaultValues,
  onSubmit
}: {
  defaultValues?: WithdrawalFormData
  onSubmit: (values: WithdrawalFormData) => void
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const {
    register,
    formState: { errors },
    handleSubmit
  } = useForm<WithdrawalFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues ?? { name: '', orderNumber: '', email: '' },
    mode: 'onChange'
  })

  const fields = [
    { key: 'name', type: 'text', autoComplete: 'name' },
    { key: 'orderNumber', type: 'text', placeholder: 'X01234' },
    { key: 'email', type: 'email', autoComplete: 'email' }
  ] as const

  const labels = {
    name: t('withdrawal.name_label'),
    orderNumber: t('withdrawal.order_number_label'),
    email: t('withdrawal.email_label')
  }

  return (
    <Form
      className="w-full max-w-96 space-y-2"
      onSubmit={handleSubmit(onSubmit)}
    >
      {fields.map((field) => {
        const error = errors[field.key]?.message
        return (
          <Input
            key={field.key}
            {...register(field.key)}
            type={field.type}
            label={labels[field.key]}
            placeholder={'placeholder' in field ? field.placeholder : undefined}
            autoComplete={
              'autoComplete' in field ? field.autoComplete : undefined
            }
            radius="sm"
            variant="bordered"
            isInvalid={!!error}
            errorMessage={error ? t(error) : undefined}
            classNames={{ inputWrapper: 'border-solid' }}
          />
        )
      })}
      <Button
        type="submit"
        variant="solid"
        radius="sm"
        size="lg"
        fullWidth
        style={{
          backgroundColor: theme.colors.primary.main,
          color: theme.colors.primary.contrastText,
          borderRadius: theme.borderRadius
        }}
      >
        {t('withdrawal.continue')}
      </Button>
    </Form>
  )
}
